import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, Plus, Tag, X } from 'lucide-react';
import { leadTagsAPI } from '../../utils/api';
import { DEFAULT_TAG_COLOR, LeadTagResponse } from '../../types/leadTag';

interface Props {
  leadId: string;
  readonly?: boolean;
}

function contrastColor(hex: string): string {
  const h = hex.replace('#', '');
  if (h.length !== 6) return '#000000';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // YIQ formula
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 140 ? '#111827' : '#ffffff';
}

export default function LeadTagChipEditor({ leadId, readonly }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce 300ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Close dropdown on outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const { data: leadTags, isLoading } = useQuery({
    queryKey: ['lead-tags', leadId],
    queryFn: async () => {
      const res = await leadTagsAPI.getLeadTags(leadId);
      return res.data;
    },
  });

  // Candidates: all tenant tags (cached) filtered client-side by search
  const { data: allTags } = useQuery({
    queryKey: ['tags-all'],
    queryFn: async () => {
      const res = await leadTagsAPI.getTags({ limit: 100 });
      return res.data.data;
    },
    enabled: isOpen,
    staleTime: 60 * 1000,
  });

  const attachedIds = useMemo(
    () => new Set((leadTags || []).map((t) => t.id)),
    [leadTags]
  );

  const candidates = useMemo(() => {
    const list = (allTags || []).filter((t) => !attachedIds.has(t.id));
    if (!debouncedSearch) return list;
    const q = debouncedSearch.toLowerCase();
    return list.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q)
    );
  }, [allTags, attachedIds, debouncedSearch]);

  const exactMatch = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return null;
    return (allTags || []).find((t) => t.name.toLowerCase() === q) || null;
  }, [allTags, debouncedSearch]);

  const attachMutation = useMutation({
    mutationFn: (tagId: string) => leadTagsAPI.attachToLead(leadId, tagId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tags', leadId] });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || "Errore nell'associazione");
    },
  });

  const detachMutation = useMutation({
    mutationFn: (tagId: string) => leadTagsAPI.detachFromLead(leadId, tagId),
    onMutate: async (tagId) => {
      await queryClient.cancelQueries({ queryKey: ['lead-tags', leadId] });
      const prev = queryClient.getQueryData<LeadTagResponse[]>(['lead-tags', leadId]);
      queryClient.setQueryData<LeadTagResponse[]>(
        ['lead-tags', leadId],
        (prev || []).filter((t) => t.id !== tagId)
      );
      return { prev };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['lead-tags', leadId], ctx.prev);
      toast.error("Impossibile rimuovere il tag");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tags', leadId] });
    },
  });

  const createAndAttachMutation = useMutation({
    mutationFn: async (name: string) => {
      const created = await leadTagsAPI.createTag({
        name,
        color: DEFAULT_TAG_COLOR,
      });
      await leadTagsAPI.attachToLead(leadId, created.data.id);
      return created.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-tags', leadId] });
      queryClient.invalidateQueries({ queryKey: ['tags-all'] });
      queryClient.invalidateQueries({ queryKey: ['tags', { scope: 'list' }] });
      setSearch('');
      toast.success('Tag creato e applicato');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    },
  });

  const handleAttach = (tagId: string) => {
    attachMutation.mutate(tagId);
    setSearch('');
    setIsOpen(false);
  };

  const handleCreate = () => {
    const name = debouncedSearch.trim();
    if (!name) return;
    if (exactMatch) {
      handleAttach(exactMatch.id);
      return;
    }
    createAndAttachMutation.mutate(name);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5">
        {isLoading && <Loader2 className="h-4 w-4 animate-spin text-gray-400" />}
        {(leadTags || []).map((tag) => {
          const fg = contrastColor(tag.color);
          return (
            <span
              key={tag.id}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{ backgroundColor: tag.color, color: fg }}
              title={tag.description || undefined}
            >
              <Tag className="h-3 w-3" />
              {tag.name}
              {!readonly && (
                <button
                  type="button"
                  onClick={() => detachMutation.mutate(tag.id)}
                  className="ml-0.5 hover:bg-black/10 rounded-full p-0.5"
                  aria-label="Rimuovi tag"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          );
        })}
        {!readonly && (
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setIsOpen(true);
              }}
              onFocus={() => setIsOpen(true)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleCreate();
                } else if (e.key === 'Escape') {
                  setIsOpen(false);
                }
              }}
              placeholder="Aggiungi tag..."
              className="px-2 py-0.5 text-xs border border-dashed border-gray-300 rounded-full focus:outline-none focus:border-blue-400 w-32"
            />
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !readonly && (
        <div className="absolute z-10 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {candidates.length === 0 && !debouncedSearch && (
            <div className="px-3 py-2 text-xs text-gray-500">
              Nessun tag disponibile. Digita per crearne uno.
            </div>
          )}
          {candidates.length === 0 && debouncedSearch && !exactMatch && (
            <button
              type="button"
              onClick={handleCreate}
              disabled={createAndAttachMutation.isPending}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <Plus className="h-4 w-4 text-green-600" />
              <span>
                Crea <strong>«{debouncedSearch}»</strong>
              </span>
            </button>
          )}
          {candidates.map((t) => {
            const fg = contrastColor(t.color);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => handleAttach(t.id)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
              >
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: t.color, color: fg }}
                >
                  <Tag className="h-3 w-3" />
                  {t.name}
                </span>
                {t.description && (
                  <span className="text-xs text-gray-500 truncate">{t.description}</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
